import React, { useEffect, useRef } from "react";
import { useMessagesPanel } from "./MessagesPanelContext";
import {
  PresenceDot,
  PresenceLabel,
  TypingIndicator,
} from "../presence/PresenceContext";
import { MessageSquare, X, ArrowLeft, Send } from "lucide-react";

const MessagesPanel = () => {
  const {
    isOpen,
    conversations,
    selectedConversation,
    messages,
    loading,
    messagesLoading,
    newMessage,
    setNewMessage,
    sending,
    currentUserId,
    view,
    closePanel,
    selectConversation,
    goBackToList,
    sendMessage,
    announceTypingInPanel,
  } = useMessagesPanel();

  const messagesEndRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isOpen) closePanel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, closePanel]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    sendMessage(newMessage);
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    announceTypingInPanel(true);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={closePanel}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div
        role="dialog"
        aria-label="Messages"
        aria-hidden={!isOpen}
        className={`fixed inset-y-0 right-0 w-[480px] max-w-[calc(100vw-2rem)] bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          {view === "conversation" && selectedConversation ? (
            <div className="flex items-center gap-2 mt-20">
              <button
                type="button"
                onClick={goBackToList}
                className="p-1.5 hover:bg-slate-100 rounded-lg"
                aria-label="Back to conversations"
              >
                <ArrowLeft size={18} className="text-slate-600" />
              </button>
              <h2 className="font-semibold text-slate-900 text-sm truncate">
                {selectedConversation.title ||
                  `Conversation ${selectedConversation.id.slice(0, 8)}`}
              </h2>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-20">
              <MessageSquare size={18} className="text-slate-600" />
              <h2 className="font-semibold text-slate-900 text-sm">Messages</h2>
            </div>
          )}
          <button
            type="button"
            onClick={closePanel}
            className="p-1.5 hover:bg-slate-100 rounded-lg"
            aria-label="Close messages"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {/* Content */}
        {view === "list" ? (
          <ConversationList
            conversations={conversations}
            loading={loading}
            currentUserId={currentUserId}
            selectedConversation={selectedConversation}
            onSelect={selectConversation}
          />
        ) : (
          <ConversationView
            messages={messages}
            messagesLoading={messagesLoading}
            currentUserId={currentUserId}
            selectedConversation={selectedConversation}
            newMessage={newMessage}
            sending={sending}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            messagesEndRef={messagesEndRef}
          />
        )}
      </div>
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Conversation List                                                  */
/* ------------------------------------------------------------------ */
const ConversationList = ({
  conversations,
  loading,
  currentUserId,
  selectedConversation,
  onSelect,
}) => {
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
        Loading conversations...
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-500 px-4">
        <MessageSquare size={32} className="text-slate-300 mb-2" />
        <p className="text-sm">No conversations yet</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
      {conversations.map((conv) => {
        const otherId = (conv.participants || []).find(
          (p) => p !== currentUserId,
        );
        const isActive = selectedConversation?.id === conv.id;
        return (
          <button
            key={conv.id}
            onClick={() => onSelect(conv)}
            className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors ${
              isActive ? "bg-blue-50 border-l-4 border-blue-500" : ""
            }`}
          >
            <div className="font-medium text-slate-900 text-sm flex items-center gap-2">
              <span className="truncate">
                {conv.title || `Conversation ${conv.id.slice(0, 8)}`}
              </span>
              {otherId && <PresenceDot userId={otherId} size="xs" />}
            </div>
            {conv.last_message && (
              <div className="text-xs text-slate-500 mt-1 truncate">
                {conv.last_message.content}
              </div>
            )}
            {otherId && <PresenceLabel userId={otherId} className="mt-1" />}
          </button>
        );
      })}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Conversation View (messages + input)                               */
/* ------------------------------------------------------------------ */
const ConversationView = ({
  messages,
  messagesLoading,
  currentUserId,
  selectedConversation,
  newMessage,
  sending,
  onInputChange,
  onSubmit,
  messagesEndRef,
}) => {
  if (messagesLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
        Loading messages...
      </div>
    );
  }

  return (
    <>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-8">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${
                msg.sender_id === currentUserId
                  ? "justify-end"
                  : "justify-start"
              }`}
            >
              <div
                className={`max-w-[75%] px-3 py-2 rounded-lg ${
                  msg.sender_id === currentUserId
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-900"
                }`}
              >
                <div className="text-sm">{msg.content}</div>
                <div
                  className={`text-[10px] mt-1 ${
                    msg.sender_id === currentUserId
                      ? "text-blue-100"
                      : "text-gray-500"
                  }`}
                >
                  {new Date(msg.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {selectedConversation && (
        <div className="px-4 min-h-[18px]">
          <TypingIndicator
            conversationId={selectedConversation.id}
            excludeUserId={currentUserId}
          />
        </div>
      )}

      {/* Message input */}
      <form onSubmit={onSubmit} className="p-3 border-t border-slate-200">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={onInputChange}
            placeholder="Type a message..."
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            disabled={sending}
            aria-label="Message text"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            aria-label="Send message"
          >
            <Send size={18} />
          </button>
        </div>
      </form>
    </>
  );
};

export default MessagesPanel;
